import { toCivilYmd } from "@/lib/utils/date/date-only";
import {
  AttendanceStatus,
  type GetAttendanceLogResponse,
  type GetMakeupResponse,
  MakeupStatus,
} from "@/openapi/generated";
import {
  attendanceLogAppointmentId,
  attendanceLogClassId,
  attendanceLogStudentId,
} from "./attendanceLogIds";
import {
  DEFAULT_MAKEUPS_EXPIRY_IN_DAYS,
  isMakeupsNoExpiry,
} from "./makeupExpirySettings";
import { isMakeupStatusActive } from "./makeupStatus";

export {
  DEFAULT_MAKEUPS_EXPIRY_IN_DAYS,
  MAKEUP_ELIGIBILITY_LOOKBACK_DAYS,
} from "./makeupExpirySettings";

function isAbsentAttendanceLog(log: GetAttendanceLogResponse): boolean {
  return log.status === AttendanceStatus.Absent;
}

function isWithinMakeupEligibilityWindow(
  log: GetAttendanceLogResponse,
  expiryDays: number,
): boolean {
  if (isMakeupsNoExpiry(expiryDays)) return true;

  const sessionDate = log.date instanceof Date ? log.date : new Date(log.date);
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - expiryDays);
  const d = new Date(sessionDate);
  d.setHours(0, 0, 0, 0);
  return d >= cutoff;
}

/** Whole days left to submit a makeup request (0 on last eligible day). Null = no expiry. */
export function makeupDaysRemainingInWindow(
  log: GetAttendanceLogResponse,
  expiryDays: number = DEFAULT_MAKEUPS_EXPIRY_IN_DAYS,
): number | null {
  if (isMakeupsNoExpiry(expiryDays)) return null;

  const sessionDate = log.date instanceof Date ? log.date : new Date(log.date);
  const sessionDay = new Date(sessionDate);
  sessionDay.setHours(0, 0, 0, 0);
  const deadline = new Date(sessionDay);
  deadline.setDate(deadline.getDate() + expiryDays);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffMs = deadline.getTime() - today.getTime();
  return Math.max(0, Math.ceil(diffMs / 86_400_000));
}

export function makeupEligibilityDateRange(expiryDays: number): {
  fromDate: Date;
  toDate: Date;
} {
  const toDate = new Date();
  toDate.setHours(12, 0, 0, 0);
  const fromDate = new Date(toDate);
  if (isMakeupsNoExpiry(expiryDays)) {
    fromDate.setFullYear(fromDate.getFullYear() - 10);
  } else {
    fromDate.setDate(fromDate.getDate() - expiryDays);
  }
  return { fromDate, toDate };
}

function hasMakeupClassSource(log: GetAttendanceLogResponse): boolean {
  return attendanceLogClassId(log) != null;
}

const MAKEUP_FALLBACK_KEY_PREFIX = "fb:";

function toDateOnlyKey(d: Date | string): string {
  const date = d instanceof Date ? d : new Date(d);
  return toCivilYmd(date) ?? toCivilYmd(dateOnlyFromDate(date)) ?? "";
}

function makeupSessionStudentId(session: GetMakeupResponse): number | null {
  const id = Number(session.student?.id as unknown);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function makeupSessionSourceClassId(session: GetMakeupResponse): number | null {
  const id =
    session._class?.id != null ? Number(session._class.id as unknown) : null;
  return id != null && Number.isFinite(id) && id > 0 ? id : null;
}

function makeupAttendanceFallbackKey(
  studentId: number,
  classId: number,
  sessionDate: Date | string,
): string {
  return `${studentId}|${classId}|${toDateOnlyKey(sessionDate)}`;
}

function makeupFallbackKeyFromSession(
  session: GetMakeupResponse,
): string | null {
  const studentId = makeupSessionStudentId(session);
  const classId = makeupSessionSourceClassId(session);
  if (studentId == null || classId == null) return null;
  return makeupAttendanceFallbackKey(
    studentId,
    classId,
    session.sourceSessionDate,
  );
}

function makeupFallbackKeyFromLog(
  log: GetAttendanceLogResponse,
): string | null {
  const studentId = attendanceLogStudentId(log);
  const classId = attendanceLogClassId(log);
  if (studentId == null || classId == null) return null;
  return makeupAttendanceFallbackKey(studentId, classId, log.date);
}

function preferMakeupSession(
  previous: GetMakeupResponse,
  next: GetMakeupResponse,
): GetMakeupResponse {
  const previousActive = isMakeupStatusActive(previous.status);
  const nextActive = isMakeupStatusActive(next.status);
  if (nextActive && !previousActive) return next;
  if (!nextActive && previousActive) return previous;
  const previousTime = new Date(previous.requestedAt).getTime();
  const nextTime = new Date(next.requestedAt).getTime();
  return nextTime >= previousTime ? next : previous;
}

function upsertMakeupLookup(
  map: Map<string, GetMakeupResponse>,
  key: string,
  session: GetMakeupResponse,
): void {
  const previous = map.get(key);
  map.set(key, previous ? preferMakeupSession(previous, session) : session);
}

export function buildMakeupByAttendanceLogId(
  sessions: GetMakeupResponse[],
): Map<string, GetMakeupResponse> {
  const map = new Map<string, GetMakeupResponse>();
  for (const session of sessions) {
    if (session.attendanceLogId) {
      upsertMakeupLookup(map, session.attendanceLogId, session);
    }
    const fallbackKey = makeupFallbackKeyFromSession(session);
    if (fallbackKey) {
      upsertMakeupLookup(
        map,
        `${MAKEUP_FALLBACK_KEY_PREFIX}${fallbackKey}`,
        session,
      );
    }
  }
  return map;
}

function resolveMakeupForAttendanceLog(
  log: GetAttendanceLogResponse,
  makeupByAttendanceLogId: Map<string, GetMakeupResponse>,
): GetMakeupResponse | undefined {
  const direct = makeupByAttendanceLogId.get(log.id);
  if (direct) return direct;
  const fallbackKey = makeupFallbackKeyFromLog(log);
  if (!fallbackKey) return undefined;
  return makeupByAttendanceLogId.get(
    `${MAKEUP_FALLBACK_KEY_PREFIX}${fallbackKey}`,
  );
}

function canRetryMakeupAfterTerminal(status: MakeupStatus): boolean {
  return status === MakeupStatus.Rejected || status === MakeupStatus.Cancelled;
}

export function classIdsThatDoNotOfferMakeup(
  classes: Iterable<{ classId?: unknown; offersMakeup?: boolean | null }>,
): Set<number> {
  const ids = new Set<number>();
  for (const classItem of classes) {
    if (classItem.offersMakeup !== false) continue;
    const id = Number(classItem.classId);
    if (Number.isFinite(id) && id > 0) ids.add(id);
  }
  return ids;
}

export function isAttendanceLogMakeupEligible(
  log: GetAttendanceLogResponse,
  makeupByAttendanceLogId: Map<string, GetMakeupResponse>,
  expiryDays: number = DEFAULT_MAKEUPS_EXPIRY_IN_DAYS,
  classesThatDoNotOfferMakeup?: ReadonlySet<number>,
  options?: { appointmentsEnabled?: boolean },
): boolean {
  if (
    options?.appointmentsEnabled === false &&
    attendanceLogAppointmentId(log) != null
  ) {
    return false;
  }
  if (!isAbsentAttendanceLog(log)) return false;
  if (!isWithinMakeupEligibilityWindow(log, expiryDays)) return false;
  if (!hasMakeupClassSource(log)) return false;
  const classId = attendanceLogClassId(log);
  if (classId != null && classesThatDoNotOfferMakeup?.has(classId)) {
    return false;
  }
  const existing = resolveMakeupForAttendanceLog(log, makeupByAttendanceLogId);
  if (existing) {
    if (isMakeupStatusActive(existing.status)) return false;
    if (existing.status === MakeupStatus.Approved) return false;
    if (!canRetryMakeupAfterTerminal(existing.status)) return false;
  }
  return true;
}

export function timeOnlyFromDate(d: Date): string {
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

export function dateOnlyFromDate(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0);
}
